import { AlertTriangle } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Chips, SectionCard, MmInput, TextIn, toggleIn } from "./Controls";
import { INVESTIGATIONS, SCREENING, MEASURE_FIELDS, MEASURE_SOURCES } from "./config";

export function InvestigationsCard({ up, setUp, slug }) {
  const otherKey = INVESTIGATIONS[slug].find((x) => x.startsWith("Other"));
  return (
    <SectionCard title={slug === "implant" ? "Investigations reviewed" : "Available investigations"}>
      <Chips testid="investigations-chips" options={INVESTIGATIONS[slug]} selected={up.investigations} onToggle={(o) => setUp({ investigations: toggleIn(up.investigations, o) })} />
      {(up.investigations || []).includes(otherKey) && <TextIn className="mt-3" value={up.other} onChange={(v) => setUp({ other: v })} placeholder="Describe other investigation (required)" testid="investigations-other" />}
      <textarea rows={2} value={up.notes || ""} onChange={(e) => setUp({ notes: e.target.value })} placeholder="Additional clinical notes (optional)" data-testid="investigations-notes" className="mt-3 w-full rounded-lg border border-[#E2E8F0] bg-white p-2.5 text-sm dark:border-[#223139] dark:bg-white/5 dark:text-white" />
    </SectionCard>
  );
}

export function ScreeningCard({ up, setUp, slug }) {
  const signs = SCREENING[slug];
  if (!signs) return null;
  const any = (up.screening || []).length > 0;
  const toggle = (o) => setUp({ screening: toggleIn(up.screening, o), screening_none: false, ack: false });
  return (
    <SectionCard title="Safety screening" subtitle="Tick any warning sign, or 'None of these'.">
      <Chips testid="screening-chips" options={signs} selected={up.screening} onToggle={toggle} />
      <label className="mt-3 flex items-center gap-2 text-sm"><Checkbox checked={!!up.screening_none} onCheckedChange={(v) => setUp({ screening_none: !!v, screening: v ? [] : up.screening, ack: false })} data-testid="screening-none" />None of these</label>
      {any && (
        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300" data-testid="screening-alert">
          <p className="flex items-center gap-1.5 font-bold"><AlertTriangle className="h-3.5 w-3.5" />Warning sign recorded — report generation will pause for urgent-referral guidance.</p>
          {(up.screening || []).some((s) => /breath|swallow/i.test(s)) && <p className="mt-1 font-bold">Difficulty breathing or swallowing: arrange emergency referral now.</p>}
          <label className="mt-2 flex items-center gap-2"><Checkbox checked={!!up.ack} onCheckedChange={(v) => setUp({ ack: !!v })} data-testid="screening-ack" />I acknowledge the urgent-referral guidance</label>
        </div>
      )}
    </SectionCard>
  );
}

export function MeasurementTable({ w, update, slug }) {
  const sites = Object.keys(w.exam?.[slug]?.sites || w.exam?.[slug]?.teeth || {}).sort();
  if (slug === "tmj") return null;
  const m = w.measurements || {};
  const set = (fdi, key, patch) => update((d) => { const cur = d.measurements || {}; const row = cur[fdi] || {}; return { ...d, measurements: { ...cur, [fdi]: { ...row, [key]: typeof patch === "object" ? { ...(row[key] || {}), ...patch } : patch } } }; });
  return (
    <SectionCard title="Typed measurements (clinician-measured)" subtitle="Per site from the chart. Pick the source for each value.">
      {!sites.length ? <p className="text-sm text-[#8B9698]">Mark sites on the Examination chart to add measurements.</p> : (
        <div className="space-y-4" data-testid="measurement-table">
          {sites.map((fdi) => (
            <div key={fdi} className="rounded-xl border border-[#E2E8F0] p-3 dark:border-[#223139]">
              <div className="mb-2 text-sm font-bold text-[#101516] dark:text-white">Site {fdi}</div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {MEASURE_FIELDS.map((f) => {
                  const v = m[fdi]?.[f.key] || {};
                  const twoD = f.cbct && ["OPG", "IOPA"].includes(v.source) && v.value;
                  return (
                    <div key={f.key}>
                      <MmInput label={f.label} value={v.value} onChange={(val) => set(fdi, f.key, { value: val })} testid={`meas-${fdi}-${f.key}`} />
                      <select value={v.source || ""} onChange={(e) => set(fdi, f.key, { source: e.target.value })} data-testid={`meas-${fdi}-${f.key}-source`} className="mt-1 h-7 w-full rounded-md border border-[#E2E8F0] bg-white px-1.5 text-[11px] dark:border-[#223139] dark:bg-white/5 dark:text-white">
                        <option value="">Source…</option>{MEASURE_SOURCES.map((s) => <option key={s}>{s}</option>)}
                      </select>
                      {twoD && <p className="mt-0.5 text-[10px] text-amber-600" data-testid={`meas-${fdi}-${f.key}-warn`}>2D estimate — verify on CBCT</p>}
                    </div>
                  );
                })}
              </div>
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                <TextIn value={m[fdi]?.slice_ref} onChange={(v) => set(fdi, "slice_ref", v)} placeholder="CBCT slice / section reference" testid={`meas-${fdi}-slice`} />
                <TextIn value={m[fdi]?.notes} onChange={(v) => set(fdi, "notes", v)} placeholder="Notes" testid={`meas-${fdi}-notes`} />
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}
